import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { NotificationBell } from "../../../components/NotificationPanel";

const navLinks = [
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Vehicles", href: "/car-hire-listing", isRoute: true },
  { label: "Invest", href: "#invest" },
  { label: "Download", href: "#download" },
];

const LandingNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrolled
            ? "rgba(10, 22, 40, 0.96)"
            : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(255,215,0,0.1)" : "none",
          boxShadow: scrolled ? "0 4px 40px rgba(0,0,0,0.4)" : "none",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <a href="#" onClick={(e) => { e?.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-3 group">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg text-[#0a1628] group-hover:scale-110 transition-transform duration-300"
                style={{ background: "linear-gradient(135deg, #ffd700 0%, #f59e0b 100%)" }}
              >
                OG
              </div>
              <span className="text-white font-bold text-xl tracking-tight">
                On<span style={{ color: "#ffd700" }}>TheGo</span>
              </span>
            </a>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks?.map((link) => (
                link?.isRoute ? (
                  <Link
                    key={link?.label}
                    to={link?.href}
                    className="px-4 py-2 rounded-xl text-white/60 text-sm font-medium hover:text-white hover:bg-white/8 transition-all duration-200"
                  >
                    {link?.label}
                  </Link>
                ) : (
                  <a
                    key={link?.label}
                    href={link?.href}
                    onClick={(e) => { e?.preventDefault(); handleNavClick(link?.href); }}
                    className="px-4 py-2 rounded-xl text-white/60 text-sm font-medium hover:text-white hover:bg-white/8 transition-all duration-200"
                  >
                    {link?.label}
                  </a>
                )
              ))}
            </div>

            {/* CTA buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/admin-dashboard"
                className="text-white/40 text-sm hover:text-white/70 transition-colors duration-200 font-medium"
              >
                Admin
              </Link>
              {/* Notification Bell */}
              <NotificationBell
                iconColor="rgba(255,255,255,0.7)"
                badgeColor="bg-red-500"
              />
              <Link
                to="/host-signup"
                className="px-4 py-2.5 rounded-xl font-semibold text-sm border border-[#ffd700]/40 text-[#ffd700] hover:bg-[#ffd700]/10 transition-all duration-300"
              >
                Become a Host
              </Link>
              <a
                href="#download"
                onClick={(e) => { e?.preventDefault(); handleNavClick("#download"); }}
                className="px-5 py-2.5 rounded-xl font-bold text-sm text-[#0a1628] transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{ background: "linear-gradient(135deg, #ffd700 0%, #f59e0b 100%)" }}
              >
                Get the App
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition-all duration-200"
              aria-label="Toggle menu"
            >
              <div className="w-5 flex flex-col gap-1.5">
                <span
                  className="block h-0.5 bg-current transition-all duration-300 origin-center"
                  style={{
                    transform: menuOpen ? "translateY(8px) rotate(45deg)" : "none",
                  }}
                />
                <span
                  className="block h-0.5 bg-current transition-all duration-300"
                  style={{ opacity: menuOpen ? 0 : 1 }}
                />
                <span
                  className="block h-0.5 bg-current transition-all duration-300 origin-center"
                  style={{
                    transform: menuOpen ? "translateY(-8px) rotate(-45deg)" : "none",
                  }}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className="lg:hidden overflow-hidden transition-all duration-300"
          style={{
            maxHeight: menuOpen ? "400px" : "0",
            opacity: menuOpen ? 1 : 0,
          }}
        >
          <div
            className="border-t border-white/10 py-4 px-4"
            style={{ background: "rgba(10, 22, 40, 0.98)", backdropFilter: "blur(20px)" }}
          >
            {navLinks?.map((link) => (
              link?.isRoute ? (
                <Link
                  key={link?.label}
                  to={link?.href}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200 font-medium"
                >
                  {link?.label}
                </Link>
              ) : (
                <a
                  key={link?.label}
                  href={link?.href}
                  onClick={(e) => { e?.preventDefault(); handleNavClick(link?.href); }}
                  className="flex items-center gap-3 px-4 py-3.5 text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-200 font-medium"
                >
                  {link?.label}
                </a>
              )
            ))}
            <div className="pt-3 mt-3 border-t border-white/10">
              <Link
                to="/host-signup"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm border border-[#ffd700]/40 text-[#ffd700] hover:bg-[#ffd700]/10 transition-all duration-300 mb-2"
              >
                🚗 Become a Host
              </Link>
              <a
                href="#download"
                onClick={(e) => { e?.preventDefault(); handleNavClick("#download"); }}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-bold text-[#0a1628] transition-all duration-300"
                style={{ background: "linear-gradient(135deg, #ffd700 0%, #f59e0b 100%)" }}
              >
                📲 Get the App Free
              </a>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default LandingNav;
